import { get, findIndex, set, isEmpty } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { AttorneyResponseStatus } from '@repo/types';
import { AppealStatus } from '@repo/types';
import { queryClient } from '@xtnd/web/providers/QueryClientProvider';
import { useQueryKeyStore } from '@xtnd/web/shared/stores';
import { invoiceApi, InvoiceSearchParams, BulkAssignAppealsClerkPayload } from '../api';
import {
  AttorneyResponsePatchPayload,
  AttorneyResponsePayload,
  DelegateUpdatePayload,
  MatterInvoice,
} from '../types';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

/**
 * Query hook for fetching a single invoice by its ID.
 *
 * @param invoiceId - The ID of the invoice to fetch.
 * @param options - Optional configuration options.
 * @returns A React Query result object containing the invoice data and query status.
 */
export const useGetInvoice = (invoiceId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => invoiceApi.getInvoice(invoiceId),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
};

/**
 * React Query hook for fetching the attorney response for a given invoice and appeal.
 *
 * @param invoiceId - The unique ID of the invoice.
 * @param appealId - The unique ID of the appeal associated with the invoice.
 * @param options - Optional configuration for the query:
 *   - enabled: Whether the query should be active (default: true).
 *
 * @returns A React Query result object containing the attorney response data and query status.
 *
 * @example
 * const { data, isLoading } = useGetAttorneyResponse(invoiceId, appealId);
 */
export function useGetAttorneyResponse(
  invoiceId: string,
  appealId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['attorneyResponse', invoiceId, appealId],
    queryFn: () => invoiceApi.getAttorneyResponse(invoiceId, appealId),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * React Query hook for submitting an attorney response for a given invoice and appeal.
 *
 * @param invoiceId - The unique identifier of the invoice.
 * @param appealId - The unique identifier of the appeal.
 * @param payload - The attorney response payload.
 * @returns A React Query result object containing the attorney response data and query status.
 */
export function useSubmitAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      payload: AttorneyResponsePayload;
    }) => invoiceApi.submitAttorneyResponse(invoiceId, appealId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Attorney response submitted successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to submit attorney response. Please try again.')
      );
    },
  });
}

/**
 * React Query hook for updating an attorney response for a specific invoice, appeal, and timekeeper.
 *
 * @returns A React Query mutation object for updating the attorney response.
 *
 * @example
 * const mutation = useUpdateAttorneyResponse();
 * mutation.mutate({
 *   invoiceId: '123',
 *   appealId: '456',
 *   timekeeperId: '789',
 *   payload: { ... } // AttorneyResponsePayload
 * });
 */
export function useUpdateAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: AttorneyResponsePayload;
    }) => invoiceApi.updateAttorneyResponse(invoiceId, appealId, timekeeperId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Attorney response submitted successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update attorney response. Please try again.')
      );
    },
  });
}

/**
 * React Query hook for patching (partially updating) an attorney response for a specific invoice, appeal, and timekeeper.
 *
 * Handles partial update requests to attorney responses, such as single-field or incremental updates.
 * On success, invalidates cached query data for the relevant attorney response and shows a success toast.
 * On error, displays an error notification with a detailed or generic message.
 *
 * @returns A React Query mutation object for patching the attorney response.
 *
 * @example
 * const mutation = usePatchAttorneyResponse();
 * mutation.mutate({
 *   invoiceId: '123',
 *   appealId: '456',
 *   timekeeperId: '789',
 *   payload: { ... } // AttorneyResponsePatchPayload
 * });
 */
export function usePatchAttorneyResponse() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: AttorneyResponsePatchPayload;
    }) => invoiceApi.patchAttorneyResponse(invoiceId, appealId, timekeeperId, payload),
    onSuccess: (_response, { payload }) => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });

      let message = `Attorney response submitted successfully!`;
      if (payload.status === AttorneyResponseStatus.EDIT_REQUESTED) {
        message = 'Attorney response revision requested successfully!';
      }
      showToast.success(message);
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update attorney response. Please try again.')
      );
    },
  });
}

export function useGetAttorneyResponses(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['attorneyResponses', invoiceId],
    queryFn: () => invoiceApi.getAttorneyResponses(invoiceId),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for Invoice Search
 * Optimized for searching invoices by invoice number
 *
 * @param params - Search parameters (invoiceNumber, limit)
 * @param options - Optional configuration options
 * @returns A React Query result object containing invoice search results
 *
 * @example
 * const { data, isLoading } = useInvoiceSearch({ invoiceNumber: '1234', limit: 5 });
 */
export function useInvoiceSearch(params: InvoiceSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['invoiceSearch', params] as const,
    queryFn: () => invoiceApi.searchInvoices(params),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1, // Fewer retries for invoice search
  });
}

/**
 * Hook for rolling back an invoice from APPEAL_SUBMITTED (BACKLOG) to PRE_APPEAL.
 *
 * @returns A React Query mutation object for rolling back the invoice.
 *
 * @example
 * const { mutateAsync: rollbackInvoice, isPending } = useRollbackInvoice();
 * await rollbackInvoice('INV-1234');
 */
export function useRollbackInvoice() {
  const queryClientHook = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');

  return useMutation({
    mutationFn: (invoiceId: string) => invoiceApi.rollbackInvoice(invoiceId),
    onSuccess: (response: any) => {
      // Update matter invoices cache
      const matterInvoiceQuery = queryClientHook.getQueryData(matterInvoicesQueryKey) ?? {};
      const matterInvoices = get(matterInvoiceQuery, 'data', []) as MatterInvoice[];

      const invoiceIndex = findIndex(
        matterInvoices,
        invoice => invoice.invoiceId === response.data.invoiceId
      );
      if (invoiceIndex !== -1) {
        const currentInvoice = matterInvoices[invoiceIndex];
        set(matterInvoices, `[${invoiceIndex}]`, {
          ...currentInvoice,
          appealStatus: AppealStatus.PRE_APPEAL,
          isCarrierInPreview: false,
          arBalance: currentInvoice.outstandingBalance ?? 0,
        });

        queryClientHook.setQueryData(matterInvoicesQueryKey, {
          ...matterInvoiceQuery,
          data: matterInvoices,
        });
      }

      // Invalidate related queries
      [appealsQueryKey, appealByClientIdQueryKey].forEach(queryKey => {
        if (!isEmpty(queryKey)) {
          queryClientHook.invalidateQueries({ queryKey });
        }
      });

      // Keep existing invalidations
      queryClient.invalidateQueries({ queryKey: ['appeals'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });

      showToast.success('Invoice rolled back successfully!');
    },
    onError: (error: any) => {
      const backendMessage = error?.response?.data?.message || error?.message || '';

      // Provide user-friendly message for SUBSEQUENT flow rollback attempts
      if (backendMessage.includes('BACKLOG') || backendMessage.includes('appeal type')) {
        showToast.error(
          'Rollback is only available for Backlog appeals. Subsequent appeals cannot be rolled back.'
        );
        return;
      }

      showToast.error(backendMessage || 'Failed to rollback invoice. Please try again.');
    },
  });
}

/**
 * Hook for updating the delegate for a specific timekeeper in an appeal.
 *
 * @returns A React Query mutation object for updating the delegate.
 *
 * @example
 * const { mutateAsync: updateDelegate, isPending } = useUpdateDelegate();
 * await updateDelegate({
 *   invoiceId: 'INV-1234',
 *   appealId: 'APL-5678',
 *   timekeeperId: 'TK-001',
 *   payload: { delegate: 'John Doe', delegateEmail: 'john@example.com' }
 * });
 */
export function useUpdateDelegate() {
  return useMutation({
    mutationFn: ({
      invoiceId,
      appealId,
      timekeeperId,
      payload,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
      payload: DelegateUpdatePayload;
    }) => invoiceApi.updateDelegate(invoiceId, appealId, timekeeperId, payload),
    onSuccess: _response => {
      const invoiceId = get(_response, 'invoiceId');
      const appealId = get(_response, 'appeal.appealId');
      queryClient.invalidateQueries({
        queryKey: ['attorneyResponse', invoiceId, appealId],
      });
      showToast.success('Delegate updated successfully!');
    },
    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update delegate. Please try again.')
      );
    },
  });
}

/**
 * Hook for bulk assigning or unassigning Appeals Clerk to/from invoices.
 *
 * @returns A React Query mutation object for bulk assignment.
 *
 * @example
 * const { mutateAsync: bulkAssign, isPending } = useBulkAssignAppealsClerk();
 * await bulkAssign({ invoiceIds: ['INV-1', 'INV-2'], assignedTo: 'clerk@example.com' });
 */
export function useBulkAssignAppealsClerk() {
  const queryClientHook = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();

  return useMutation({
    mutationFn: (payload: BulkAssignAppealsClerkPayload) =>
      invoiceApi.bulkAssignAppealsClerk(payload),

    onSuccess: (_response, payload) => {
      const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
      const appealByGroupIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-group-id');

      // Helper to update assignedTo for matching invoices
      const updateAssignedTo = (data: any[]) =>
        data.map(item =>
          payload.invoiceIds.includes(item.invoiceId)
            ? { ...item, assignedTo: payload.assignedTo || null }
            : item
        );

      // Update main appeals cache
      if (!isEmpty(appealsQueryKey)) {
        queryClientHook.setQueryData(appealsQueryKey, (oldData: any) => {
          if (!oldData) return oldData;

          // Paginated response
          if (oldData.data && Array.isArray(oldData.data)) {
            return {
              ...oldData,
              data: updateAssignedTo(oldData.data),
            };
          }

          // Simple array
          if (Array.isArray(oldData)) {
            return updateAssignedTo(oldData);
          }

          return oldData;
        });
      }

      // Update appeals by group ID cache (for client/attorney detail view)
      if (!isEmpty(appealByGroupIdQueryKey)) {
        queryClientHook.setQueryData(appealByGroupIdQueryKey, (oldData: any) => {
          if (!oldData) return oldData;

          // Paginated response
          if (oldData.data && Array.isArray(oldData.data)) {
            return {
              ...oldData,
              data: updateAssignedTo(oldData.data),
            };
          }

          // Simple array
          if (Array.isArray(oldData)) {
            return updateAssignedTo(oldData);
          }

          return oldData;
        });
      }
    },

    onError: (error: any) => {
      showToast.error(getApiErrorMessage(error, 'Failed to assign invoices. Please try again.'));
    },
  });
}
